import React, { useEffect, useMemo, useState } from "react";
import DataTable, { DATA_TABLE_HIGHLIGHTED_ROW_CLASS } from "./DataTable";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import ProviderEnum from "../enum/Provider.enum";
import { isAccountOlderThanThreshold } from "../utils/leadsUtils";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { GridRenderCellParams } from "@mui/x-data-grid/models/params/gridCellParams";
import { GridRowClassNameParams } from "@mui/x-data-grid/models/params/gridRowParams";
import {
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import apiClient from "../utils/apiClient";

type AgentLeadsExtraProps = {
  agent: {
    id: number;
    fio?: string;
    user_id_tn?: string;
  };
};

const AgentLeadsExtra = ({ agent }: AgentLeadsExtraProps) => {
  const { t } = useTranslation();

  const provider = useSelector((state: RootState) => state.app.provider);
  const [utmSource, setUtmSource] = useState<string>();
  const [utmSources, setUtmSources] = useState<string[]>();
  const [utmSourcesLoading, setUtmSourcesLoading] = useState(false);

  const columns = useMemo<GridColDef[]>(() => {
    const result: GridColDef[] = [
      {
        field: "account_number",
        headerName: t("agentLeadsExtra.columns.accountNumber"),
        flex: 1,
      },
      {
        field: "client_initials",
        headerName: t("agentLeadsExtra.columns.clientFio"),
        flex: 1,
      },
      {
        field: "manager_initials",
        headerName: t("agentLeadsExtra.columns.managerFio"),
        flex: 1,
      },
      {
        field: "status",
        headerName: t("agentLeadsExtra.columns.status"),
        flex: 1,
      },
      {
        field: "date_open_real",
        headerName: t("agentLeadsExtra.columns.openDate"),
        flex: 1,
        valueFormatter: (value: string) =>
          value ? dayjs(value).format("DD.MM.YYYY") : "",
        renderCell: (params: GridRenderCellParams) => {
          const formatted = params.value
            ? dayjs(params.value).format("DD.MM.YYYY")
            : "";

          // Подсветка "старых" счетов, правило актуально только для Беларуси
          // (там же, где уже исключаются счета старше 2 лет при расчётах).
          if (
            provider !== ProviderEnum.BELARUS ||
            !isAccountOlderThanThreshold(params.value)
          ) {
            return formatted;
          }

          return (
            <Tooltip
              title={t("agentLeadsExtra.oldAccountTooltip")}
              followCursor
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  height: "100%",
                }}
              >
                <span>{formatted}</span>
                <WarningAmberIcon color="warning" sx={{ fontSize: 17 }} />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "utm_source",
        headerName: t("agentLeadsExtra.columns.source"),
        flex: 1,
      },
    ];

    if (provider === ProviderEnum.BELARUS) {
      result.splice(2, 0, {
        field: "citizenship_code",
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          if (params.value === "BY") {
            return (
              <Tooltip
                title={t("agentLeadsExtra.belarusResident")}
                followCursor
              >
                <img
                  src="/images/flags/by.png"
                  width={30}
                  height={15}
                  alt="BY"
                  style={{ borderRadius: 2, verticalAlign: "middle" }}
                />
              </Tooltip>
            );
          }
          return null;
        },
      });
    }

    return result;
  }, [provider, t]);

  const getRowClassName = (params: GridRowClassNameParams) =>
    provider === ProviderEnum.BELARUS &&
    isAccountOlderThanThreshold(params.row.date_open_real)
      ? DATA_TABLE_HIGHLIGHTED_ROW_CLASS
      : "";

  useEffect(() => {
    const getUtmSources = async () => {
      try {
        setUtmSourcesLoading(true);
        const response = await apiClient.get("/agents/leads_utm_sources", {
          params: { agentId: agent.id },
        });
        setUtmSources(response?.data?.items ?? []);
      } catch (error) {
        console.error("Ошибка загрузки источников лидов", error);
      } finally {
        setUtmSourcesLoading(false);
      }
    };

    if (agent?.id) {
      getUtmSources();
    }
  }, [agent?.id]);

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2, px: 2 }}>
        <FormControl
          sx={{ minWidth: 150 }}
          size="small"
          disabled={utmSourcesLoading}
        >
          <InputLabel id="agent-leads-utm-label">
            {t("agentLeadsExtra.source")}
          </InputLabel>

          <Select
            labelId="agent-leads-utm-label"
            value={utmSource}
            label={t("agentLeadsExtra.source")}
            size="small"
            onChange={(e) => setUtmSource(e.target.value)}
            disabled={utmSourcesLoading}
            input={
              <OutlinedInput
                size="small"
                label={t("agentLeadsExtra.source")}
                endAdornment={
                  utmSourcesLoading && (
                    <InputAdornment position="end" sx={{ mr: 2 }}>
                      <CircularProgress size={20} />
                    </InputAdornment>
                  )
                }
              />
            }
          >
            <MenuItem value="">{t("agentLeadsExtra.any")}</MenuItem>

            {utmSources?.map((source) => (
              <MenuItem key={source} value={source}>
                {source}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <DataTable
        columns={columns}
        path="/agents/leads"
        queryParams={{
          agentId: agent.id,
          utm_source: utmSource || null,
        }}
        getRowClassName={getRowClassName}
      />
    </>
  );
};

export default AgentLeadsExtra;
